import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { isEmailSignInLink, signInWithEmail } from "../lib/firebase";
import { routes } from "../lib/routes";
import { useInstrumentation } from "../hooks/useInstrumentation";
import { storageKeys } from "../lib/storage";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";

export default function Verify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const instrumentation = useInstrumentation();
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    // Capture returnUrl before any async operations (URL may change after sign-in)
    const returnUrl = searchParams.get("returnUrl");
    const destination = returnUrl?.startsWith("/") ? returnUrl : routes.to.home();

    const verify = async () => {
      const url = window.location.href;

      // Check if this is a sign-in link
      if (!isEmailSignInLink(url)) {
        setError("Invalid sign-in link");
        setVerifying(false);
        return;
      }

      // Get email from localStorage
      let email = localStorage.getItem(storageKeys.emailForSignIn);

      // If email is not in localStorage, prompt user to enter it
      if (!email) {
        email = window.prompt("Please provide your email for confirmation");
      }

      if (!email) {
        setError("Email is required to complete sign-in");
        setVerifying(false);
        return;
      }

      try {
        // Sign in with email link
        await signInWithEmail(email, url);

        instrumentation.track("Magic Link Verified");

        // Clear email from storage
        localStorage.removeItem(storageKeys.emailForSignIn);

        navigate(destination, { replace: true });
      } catch (err) {
        const error = err as Error;
        console.error("Sign-in error:", error);
        setError(error.message || "Failed to sign in. Please try again.");
        setVerifying(false);
      }
    };

    verify();
  }, [navigate, instrumentation, searchParams]);

  if (verifying) {
    return (
      <div className="min-h-full flex items-center justify-center p-4 bg-background">
        <Card className="p-10 max-w-lg w-full">
          <h1 className="text-2xl font-semibold mb-4 text-foreground">Verifying...</h1>
          <p className="text-muted-foreground">Please wait while we sign you in.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full flex items-center justify-center p-4 bg-background">
      <Card className="p-10 max-w-lg w-full">
        <h1 className="text-2xl font-semibold mb-4 text-foreground">Sign-in failed</h1>
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button asChild size="lg">
          <Link to={routes.to.auth.signIn()}>Try again</Link>
        </Button>
      </Card>
    </div>
  );
}
